/* eslint-disable no-restricted-globals */
/* eslint-disable arrow-body-style */
import React, { useCallback, useMemo, useState } from 'react';
import { FormControl,
  InputLabel,
  FormHelperText,
  makeStyles,
  Input,
  Theme,
  InputAdornment } from '@material-ui/core';
import { FieldHookConfig, FieldValidator, useField } from 'formik';
import { fade } from '@material-ui/core/styles';
import _ from 'lodash';


interface InputStylesProps {
  error: boolean,
  minWidth?: string,
  maxWidth?: string,
  labelHidden: boolean
}

interface FormStyleProps {
  marginTop: string
}

const useFormStyles = makeStyles<Theme, FormStyleProps>((theme: Theme) => ({
  root: {
    '& input::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      '-moz-appearance': 'none',
      margin: 0
    },
    marginTop: (props) => props.marginTop,
    '& input[type=number]': {
      '-moz-appearance': 'textfield',
      '-webkit-appearance': 'textfield'
    }
  }
}));

const useInputStyles = makeStyles<Theme, InputStylesProps>((theme: Theme) => ({
  root: {
    border: (props) => (props.error ? '1px solid #EA6172' : '1px solid #D0D6DA'),
    borderRadius: 4,
    padding: '2px 8px',
    'label + &': {
      marginTop: '20px'
    },
    '& .MuiInputAdornment-positionEnd': {
      marginLeft: '2px'
    }
  },
  underline: {
    '&:before': {
      content: 'none'
    },
    '&:after': {
      content: 'none'
    }
  },
  input: {
    minWidth: (props) => props.minWidth,
    maxWidth: (props) => props.maxWidth,
    fontSize: 16,
    lineHeight: '150%',
    padding: '8px 2px 8px 8px',
    fontWeight: 300,
    textAlign: 'right',
    transition: theme.transitions.create(['border-color', 'box-shadow'], { duration: 250, delay: 0 }),
    '&:focus': {
      boxShadow: `${fade(theme.palette.primary.main, 0.25)} 0 0 0 0`,
      borderColor: theme.palette.primary.main
    }
  }
}));

const useLabelStyles = makeStyles((theme: Theme) => ({
  root: {
    fontWeight: 500,
    fontSize: 14,
    lineHeight: '150%',
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(0)
  },
  formControl: {
    transform: 'none'
  },
  animated: {
    transition: 'none'
  }
}));


const useHelperTextStyles = makeStyles((theme: Theme) => ({
  root: {
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(1),
    maxWidth: '200px'
  }
}));

export const mapNanToNull = (value: any) => (isNaN(parseFloat(`${value}`)) ? null : parseFloat(`${value}`));


interface DollarInputProps {
  id: string,
  label?: string,
  placeholder: string,
  fullWidth?: boolean,
  helperText?: string,
  minWidth?: string,
  maxWidth?: string,
  disabled?: boolean,
  validate?: FieldValidator
  minValue?: number
  step?: number
  maxLength?: number
  autoFocus?: boolean
  allowNegative?: boolean
  marginTop?: string
}

const DollarInput: React.FC<DollarInputProps> = (
  {
    fullWidth = false,
    id,
    label = undefined,
    placeholder,
    minWidth,
    maxWidth,
    disabled = false,
    validate = undefined,
    minValue,
    step = 1,
    maxLength,
    autoFocus = false,
    allowNegative = false,
    marginTop = '8px'
  }
) => {
  const hookConfig:FieldHookConfig<number|null> = { name: id, validate, type: 'currency' };
  const [field, meta, helper] = useField(hookConfig);

  const { onBlur } = field;
  const { error, value } = meta;
  const { setValue } = helper;

  const formStyles = useFormStyles({ marginTop });
  const inputStyles = useInputStyles({ error: !!error, minWidth, maxWidth, labelHidden: !label });
  const labelStyles = useLabelStyles();
  const helperTextStyles = useHelperTextStyles();

  const inputIsAllowed = useMemo(() => {
    const numSet = new Set<string>(['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']);
    if (allowNegative) {
      numSet.add('-');
    }
    return (input: string) => {
      return numSet.has(input);
    };
  }, [allowNegative]);

  const onKeyHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!inputIsAllowed(event.key)) {
      event.preventDefault();
    } else {
      // only happens when key is not backspace
      setEventIsInsert(true);
    }
  };

  const [minusSign, setMinusSign] = useState<'-'| undefined>();
  const [lastSelectionStart, setLastSelectionStart] = useState<number>(0);
  const [lastSelectionEnd, setLastSelectionEnd] = useState<number>(0);
  const [eventIsInsert, setEventIsInsert] = useState(false);
  const [numCommas, setNumCommas] = useState(Math.trunc((`${Math.trunc(Math.abs(value || 0))}`.length - 1) / 3));
  const onChangeHandler: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event: React.ChangeEvent<HTMLInputElement>) => {
    // keep track of where the cursor should be
    let caretStart = event.target.selectionStart;
    let caretEnd = event.target.selectionEnd;
    let val = event.target.value.replaceAll(',', '');
    if (val.length > 1) {
      // '-' can be a valid character, but it only makes sense at the beginning
      const removeMinus = val.slice(1, val.length).replaceAll('-', '');
      val = `${val.slice(0, 1)}${removeMinus}`;
    }
    const valLength = `${Math.abs(parseInt(val))}`.length - 1;
    const newNumCommas = Math.trunc((valLength) / 3);
    const addComma = newNumCommas > numCommas;
    const removeComma = newNumCommas < numCommas;

    // just set value to '' if user tries to paste some nonsense,
    if (isNaN(parseFloat(val))) {
      val = '';
    }

    if (!(event.target.value === '-')) {
      setMinusSign(undefined);
    } else {
      setMinusSign('-');
    }


    if (caretEnd != null && addComma && eventIsInsert) {
      caretEnd += 1;
    }
    if (caretStart != null && addComma && eventIsInsert) {
      caretStart += 1;
    }
    if (caretEnd != null && removeComma && !eventIsInsert) {
      caretEnd -= 1;
    }
    if (caretStart != null && removeComma && !eventIsInsert) {
      caretStart -= 1;
    }

    if (caretStart != null) {
      setLastSelectionStart(caretStart);
    }
    if (caretEnd != null) {
      setLastSelectionEnd(caretEnd);
    }
    setEventIsInsert(false);
    setNumCommas(newNumCommas);
    setValue(mapNanToNull(val));
  };

  const inputRef = useCallback((input: HTMLInputElement) => {
    if (input) {
      input.setSelectionRange(lastSelectionStart, lastSelectionEnd);
    }
  }, [lastSelectionStart, lastSelectionEnd]);

  const formatValue = (v?: string | number | null) => {
    if (v == null || v === '') { return `${minusSign || ''}`; }
    return Number(`${v}`.replaceAll(',', '')).toLocaleString('en-US', { maximumFractionDigits: 0 });
  };

  return (
    <FormControl
      fullWidth={fullWidth}
      placeholder={placeholder}
      classes={formStyles}
      error={!!error}
      disabled={disabled}
    >

      {
        label && (
        <InputLabel htmlFor={id} classes={labelStyles}>
          {label}
        </InputLabel>
        )
      }
      <Input
        classes={inputStyles}
        type='text'
        placeholder={placeholder}
        id={id}
        name={id}
        inputRef={inputRef}
        value={formatValue(value)}
        onBlur={onBlur}
        onChange={onChangeHandler}
        onKeyPress={onKeyHandler}
        inputProps={{ min: minValue, step, maxLength }}
        autoFocus={autoFocus}
        startAdornment={<InputAdornment position='start'>$</InputAdornment>}
        endAdornment={<InputAdornment position='end'>.00</InputAdornment>}
      />
      {error ? <FormHelperText classes={helperTextStyles}>{error}</FormHelperText> : null}
    </FormControl>
  );
};

export default DollarInput;
